import { useState, useEffect } from 'react';
import {
    Button,
    Form,
    Input,
    Message,
    Modal,
    ModalActions,
    ModalContent,
    ModalHeader,
} from 'semantic-ui-react';
import { LoadingWrapper, ReactTable, SafeButton } from '../common';
import { canEditAll } from '../auth';
import { listDFNs, createDFN, updateDFN, deleteDFN } from './dfnService';

function DFNModal({ open, onClose, onSuccess, dfn }) {
    const isEdit = !!dfn;
    const [name, setName] = useState(dfn ? dfn.name : '');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    function handleClose() {
        setName(dfn ? dfn.name : '');
        setError('');
        onClose();
    }

    async function handleSubmit() {
        if (!name.trim()) {
            setError('Name is required.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const resp = isEdit
                ? await updateDFN(dfn.id, name.trim())
                : await createDFN(name.trim());

            if (resp.status >= 400) {
                const body = await resp.json().catch(() => null);
                const msg =
                    body?.name?.[0] ||
                    body?.detail ||
                    `Error ${resp.status}`;
                setError(msg);
            } else {
                onSuccess();
                handleClose();
            }
        } catch {
            setError('An unexpected error occurred.');
        } finally {
            setLoading(false);
        }
    }

    return (
        <Modal open={open} onClose={handleClose} size="small">
            <ModalHeader>{isEdit ? 'Edit DFN' : 'Add DFN'}</ModalHeader>
            <ModalContent>
                <Form>
                    <Form.Field>
                        <label>Name</label>
                        <Input
                            placeholder="e.g. 0869CH_03"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </Form.Field>
                    {error && <Message negative>{error}</Message>}
                </Form>
            </ModalContent>
            <ModalActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancel
                </Button>
                <Button primary onClick={handleSubmit} loading={loading} disabled={loading}>
                    {isEdit ? 'Save' : 'Create'}
                </Button>
            </ModalActions>
        </Modal>
    );
}

export const DFNList = () => {
    const [dfns, setDfns] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [editingDFN, setEditingDFN] = useState(null);

    async function loadDFNs() {
        setLoaded(false);
        setError('');
        try {
            const resp = await listDFNs();
            if (resp.ok) {
                const data = await resp.json();
                setDfns(data);
            } else {
                setError('Failed to load DFNs.');
            }
        } catch {
            setError('Failed to load DFNs.');
        } finally {
            setLoaded(true);
        }
    }

    useEffect(() => {
        loadDFNs();
    }, []);

    async function handleDelete(dfn) {
        setError('');
        setSuccess('');
        try {
            const resp = await deleteDFN(dfn.id);
            if (resp.status === 204) {
                setSuccess(`DFN "${dfn.name}" deleted.`);
                loadDFNs();
            } else {
                setError(`Failed to delete "${dfn.name}".`);
            }
        } catch {
            setError(`Failed to delete "${dfn.name}".`);
        }
    }

    function openCreate() {
        setEditingDFN(null);
        setModalOpen(true);
    }

    function openEdit(dfn) {
        setEditingDFN(dfn);
        setModalOpen(true);
    }

    function handleModalSuccess() {
        setSuccess(editingDFN ? 'DFN updated successfully.' : 'DFN created successfully.');
        loadDFNs();
    }

    const columns = [
        {
            Header: 'Name',
            accessor: 'name',
        },
        {
            Header: 'Created',
            accessor: 'creationTime',
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString('en-US') : '—',
        },
        {
            Header: 'Last Modified',
            accessor: 'modifiedTime',
            Cell: ({ value }) => value ? new Date(value).toLocaleDateString('en-US') : '—',
        },
        ...(canEditAll()
            ? [
                  {
                      Header: 'Actions',
                      id: 'actions',
                      disableFilters: true,
                      Cell: ({ row }) => (
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                              <Button
                                  size="small"
                                  onClick={() => openEdit(row.original)}
                              >
                                  Edit
                              </Button>
                              <SafeButton
                                  size="small"
                                  style={{ backgroundColor: 'var(--delete)', color: 'white' }}
                                  submitAction={() => handleDelete(row.original)}
                                  submitText="Delete"
                              />
                          </div>
                      ),
                  },
              ]
            : []),
    ];

    return (
        <div>
            {error && (
                <Message compact negative style={{ width: 'fit-content' }}>
                    {error}
                </Message>
            )}
            {success && (
                <Message compact positive style={{ width: 'fit-content' }}>
                    {success}
                </Message>
            )}
            <LoadingWrapper loading={!loaded} text="Loading DFNs">
                <ReactTable
                    className="channel-table"
                    data={dfns}
                    columns={columns}
                />
                {canEditAll() && (
                    <div style={{ paddingTop: '1rem', paddingBottom: '2rem' }}>
                        <Button primary onClick={openCreate}>
                            Add DFN
                        </Button>
                    </div>
                )}
            </LoadingWrapper>

            <DFNModal
                key={editingDFN ? `edit-${editingDFN.id}` : 'create'}
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                onSuccess={handleModalSuccess}
                dfn={editingDFN}
            />
        </div>
    );
};
